import React, { forwardRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import SlidePanel from './SlidePanel';
import useViewport from '../hooks/useViewport';
import { cn } from './cn';

/** Conversation workspace geometry; the caller supplies records and actions. */
export default function ChatLayout({ sidebar, mobileOpen, onCloseMobile, sidebarVisible = true, children, ...props }) {
  const { width } = useViewport();
  const mobile = width < 768;
  // Keep the conversation list mounted while its drawer closes or changes sides.
  const [sidebarRoot] = useState(() => document.createElement('div'));
  const attachSidebar = useCallback(node => {
    if (node) node.appendChild(sidebarRoot);
  }, [sidebarRoot]);
  sidebarRoot.className = 'flex h-full min-h-0 flex-col bg-bun-chat-sidebar [scrollbar-color:var(--bun-line-strong)_transparent] [&_*]:[scrollbar-width:thin]';
  return <section {...props} className="bun flex h-full min-h-0 w-full overflow-hidden font-instrument">
    {createPortal(sidebar, sidebarRoot)}
    {mobile ? <SlidePanel open={mobileOpen} onClose={onCloseMobile} side="left" width={320} zIndex={80} title="Conversations" closeLabel="Close conversation list" bodyClassName="p-0 flex min-h-0 flex-1 flex-col"><div ref={attachSidebar} className="h-full min-h-0" /></SlidePanel>
      : sidebarVisible && <aside ref={attachSidebar} className="flex w-[272px] shrink-0 flex-col border-r border-bun-line-soft bg-bun-chat-sidebar" aria-label="Conversation sidebar" />}
    <main className="flex min-h-0 min-w-0 flex-1 flex-col bg-bun-chat-canvas">{children}</main>
  </section>;
}

function Header({ children, actions, leading }) {
  return <header className="flex min-h-[60px] shrink-0 flex-wrap items-center gap-2 border-b border-bun-line-soft bg-bun-chat-canvas px-[18px] py-3 md:flex-nowrap md:gap-3 md:px-6">
    {leading && <div className="md:hidden">{leading}</div>}
    <div className="min-w-0 flex-1">{children}</div>
    {actions && <div className="flex w-full shrink-0 items-center justify-end gap-[6px] md:w-auto">{actions}</div>}
  </header>;
}

const Messages = forwardRef(function Messages({ children, className, ...props }, ref) {
  return <div {...props} ref={ref} className={cn('relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-[18px] [scrollbar-width:thin] [scrollbar-color:var(--bun-line-strong)_transparent] md:px-6', className)}>
    <div className="mx-auto w-full max-w-[760px] space-y-8 py-5">{children}</div>
  </div>;
});

function Dock({ children }) {
  return <div className="shrink-0 px-[18px] pt-3 pb-[max(16px,env(safe-area-inset-bottom))] md:px-6"><div className="mx-auto w-full max-w-[760px]">{children}</div></div>;
}

function Brand({ small = false, working = false }) {
  return <span aria-hidden="true" className={cn('relative inline-flex shrink-0 items-center justify-center rounded-bun bg-bun-ink font-instrument font-bold text-bun-on-ink', small ? 'h-[26px] w-[26px] text-bun-body' : 'h-[30px] w-[30px] text-bun-heading')}>
    P
    {working && <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-bun-blue ring-2 ring-bun-chat-sidebar" />}
  </span>;
}

function Status({ icon, children }) {
  return <div role="status" aria-live="polite" className="flex max-w-full items-start gap-[14px] py-3 font-instrument text-[14px] leading-[26px] text-bun-stone">
    {icon && <span className="flex h-[26px] shrink-0 items-center">{icon}</span>}<span className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">{children}</span>
  </div>;
}

function Metadata({ children, className }) {
  return <div className={cn('font-instrument text-bun-meta leading-6 text-bun-faint', className)}>{children}</div>;
}

ChatLayout.Header = Header;
ChatLayout.Messages = Messages;
ChatLayout.Dock = Dock;
ChatLayout.Brand = Brand;
ChatLayout.Status = Status;
ChatLayout.Metadata = Metadata;
